import { ITableViewCell, ITableViewColumn, IUpdateTableViewCell } from 'types'
import { ISpreadsheetData, ITransposedResult } from 'components/spreadsheet/types'
import { FIELD_COLUMN_ID, mapTransposedCells } from 'components/spreadsheet/helpers/transpose'
import { transformStringToCorrectFormat } from 'components/spreadsheet/helpers/paste'
import { ViewAdapterResult } from './types'

const ARRAY_KINDS = new Set(['vote', 'attachment', 'multilink', 'multiselect'])

export function createTransposeAdapter(
  spreadsheetData: ISpreadsheetData,
  transposedResult: ITransposedResult
): ViewAdapterResult {
  const coordinateMap = transposedResult.coordinateMap

  return {
    effectiveRows: transposedResult.rows,
    effectiveTotalRows: transposedResult.rows.length,
    effectiveColumnIdToIndex: transposedResult.columnIdToIndex,
    effectiveIndexToColumnId: transposedResult.indexToColumnId,
    effectiveColumns: transposedResult.columns,

    rowsViewportOverrides: {
      groupBy: [],
      disableNewRow: true,
      unpackMultiselectGroupView: false,
      treeGroupView: false,
      columnStore: null,
      visibleBitmap: null,
      visibleCount: transposedResult.rows.length
    },

    columnsViewportOverrides: {
      frozenIndex: spreadsheetData.viewDetails.frozenIndex,
      rawGroupByColumns: [],
      hiddenColumnIds: [],
      treeGroupView: false
    },

    effectiveSpreadsheetData: {
      ...spreadsheetData,
      rows: transposedResult.rows,
      viewDetails: {
        ...spreadsheetData.viewDetails,
        columns: transposedResult.columns
      },
      columnIdToIndex: transposedResult.columnIdToIndex,
      indexToColumnId: transposedResult.indexToColumnId,
      viewContext: {
        originalColumns: spreadsheetData.viewDetails.columns,
        originalRows: spreadsheetData.rows,
        originalColumnIdToIndex: spreadsheetData.columnIdToIndex,
        originalColumnStore: spreadsheetData.columnStore,
        coordinateMap,
        truncations: transposedResult.truncations,
        // The leading "field" column holds the original column names — it has no
        // source cell to write to (mapTransposedCells drops it), so it's read-only.
        readOnlyColumns: new Set([FIELD_COLUMN_ID])
      },
      columnStore: null,
      totalRows: transposedResult.rows.length,
      totalRowsOnServer: transposedResult.rows.length,
      visibleBitmap: null,
      visibleCount: transposedResult.rows.length,
      rowStreamComplete: true
    },

    mapCellsForWrite: (cells: IUpdateTableViewCell) => mapTransposedCells(cells, coordinateMap),

    transformPastedCells: (cells: ITableViewCell[], viewColumns: ITableViewColumn[]) => {
      for (const cell of cells) {
        const originalCol = viewColumns.find((c) => c.publicId === cell.columnId)
        if (originalCol && ARRAY_KINDS.has(originalCol.kind)) {
          cell.value = transformStringToCorrectFormat(cell.value as string | null, originalCol.kind)
        }
      }
    },

    columnStoreForCopy: null,

    getRenderedRows: () => transposedResult.rows,

    rowsForSelectionCheck: transposedResult.rows,
    bitmapForSelectionCheck: null,

    coordinateMap
  }
}
